import { DownloadOutlined, UploadOutlined } from '@ant-design/icons'
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Table,
  Upload,
} from '@surecompanies/core_components'
import type { ColumnType } from 'antd/es/table/interface'
import type { UploadFile } from 'antd/es/upload/interface'
import React, { useEffect, useState } from 'react'
import {
  CsvRowError,
  useUploadBenchmarkSnapshot,
} from '../hooks/useUploadBenchmarkSnapshot'
import { downloadBenchmarkSnapshotTemplate } from '../utils/downloadBenchmarkSnapshotTemplate'

const MAX_FILE_SIZE_BYTES = 1_048_576

interface Props {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const ERROR_COLUMNS: ColumnType<CsvRowError>[] = [
  { title: 'Row', dataIndex: 'row', key: 'row', width: 70 },
  { title: 'Field', dataIndex: 'field', key: 'field', width: 160 },
  { title: 'Message', dataIndex: 'message', key: 'message' },
]

export function UploadBenchmarkSnapshotModal({
  open,
  onClose,
  onSuccess,
}: Props) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const { mutate, isPending, rowErrors, reset } = useUploadBenchmarkSnapshot()

  // On re-open after errors: clear mutation state but preserve form values for retry
  useEffect(() => {
    if (open && rowErrors.length > 0) {
      reset()
    }
  }, [open]) // intentionally only on open toggle

  const selectedFile = fileList[0]?.originFileObj ?? null

  const handleBeforeUpload = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      void message.error(
        'File exceeds the 1 MB limit. Please upload a smaller file.'
      )
      return Upload.LIST_IGNORE as unknown as boolean
    }
    return false // prevent auto-upload
  }

  const handleSubmit = () => {
    if (!selectedFile || !title.trim()) return
    mutate(
      {
        file: selectedFile,
        title: title.trim(),
        description: description.trim() || undefined,
      },
      {
        onSuccess: () => {
          onClose()
          onSuccess?.()
          setTitle('')
          setDescription('')
          setFileList([])
        },
      }
    )
  }

  const isSubmitDisabled = !title.trim() || !selectedFile || isPending

  return (
    <Modal
      title="Upload Benchmark Snapshot"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose={false}
      width={640}
    >
      <Form layout="vertical">
        <Form.Item label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. 2025 Silver Benchmark"
            disabled={isPending}
          />
        </Form.Item>

        <Form.Item label="Description">
          <Input.TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={3}
            disabled={isPending}
          />
        </Form.Item>

        <Form.Item
          label="CSV File"
          required
          extra={
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={downloadBenchmarkSnapshotTemplate}
              style={{ padding: 0 }}
            >
              Download template
            </Button>
          }
        >
          <Upload.Dragger
            accept=".csv"
            maxCount={1}
            fileList={fileList}
            beforeUpload={handleBeforeUpload}
            onChange={({ fileList: newList }) => setFileList(newList)}
            disabled={isPending}
          >
            <p>
              <UploadOutlined />
            </p>
            <p>Click or drag a CSV file here to upload</p>
            <p style={{ color: '#888', fontSize: 12 }}>Max size: 1 MB</p>
          </Upload.Dragger>
        </Form.Item>

        {rowErrors.length > 0 && (
          <Form.Item label="Validation Errors">
            <Table<CsvRowError>
              dataSource={rowErrors}
              columns={ERROR_COLUMNS}
              rowKey={(r) => `${r.row}-${r.field}-${r.message}`}
              size="small"
              pagination={false}
              scroll={{ y: 240 }}
            />
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button
            style={{ marginRight: 8 }}
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={isPending}
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
            icon={<UploadOutlined />}
          >
            Upload
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}
